import type { ReactNode } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    styled,
    IconButton,
    DialogTitle,
    lighten,
} from '@mui/material';
import type { DialogProps } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useIntl } from 'react-intl';
import { getThemeColor, useIsScreenSmallerThanBreakpoint } from '@/muiTheme';
import { useScrollToTop } from '@/services/useScrollToTop';
import {
    closeDialogMessage,
    doneMessage,
    saveButtonMessage,
} from '../common-messages';
import { insidePaperFullWidthAndHorizontalPaddingStyles, Paper } from './Paper';
import { TitleAndSubTitle, SubTitle } from './TitleAndSubTitle';
import type { TitleAndSubTitleProps } from './TitleAndSubTitle';
import { FormButtons } from './FormButtons';
import type { FormButtonsProps } from './FormButtons';
import { ArrowRightIcon } from './icons';
import { formButtonClasses } from './FormButton.classes.ts';

const Root = styled(Paper)({
    paddingBottom: '0px!important',
    position: 'sticky', // This does not seem to do anything really
    top: 0,
});
const Content = styled('div')<{ fullHeight?: boolean }>(
    ({ theme, fullHeight }) => ({
        //240px is to make adjust for titles and buttons
        minHeight: fullHeight ? `calc(100vh - 240px)` : undefined,
        padding: theme.spacing(3, 0),
    }),
);

const FormPageFooter = styled(Paper)(({ theme, elevation }) => ({
    marginTop: theme.spacing(2),
    position: 'sticky', // Only guide uses sticky?
    bottom: 0,
    zIndex: 2, // Ensure we are above all components. Specifically "Here maps"
    ...insidePaperFullWidthAndHorizontalPaddingStyles(theme),

    borderTop: `1px solid ${getThemeColor(theme)('boxShadowBorder')}`,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,

    '&:before': {
        backgroundImage: `linear-gradient(to top, ${lighten(
            theme.palette.background.paper,
            (elevation ?? 1) * 0.05,
        )}, 50%, transparent)`,
        content: '""',
        display: 'block',
        height: theme.spacing(2),
        top: theme.spacing(-2),
        left: 0,
        marginTop: '-1px',
        width: '100%',
        position: 'absolute',
    },
}));
const FormPageFooterContent = styled('div')(() => ({
    // paddingTop: theme.spacing(3),
    // paddingBottom: theme.spacing(3),
    // [theme.breakpoints.down('lg')]: {
    //     paddingTop: theme.spacing(2),
    //     paddingBottom: theme.spacing(2),
    // },
    // [theme.breakpoints.down('md')]: {
    //     paddingTop: theme.spacing(1),
    //     paddingBottom: theme.spacing(1),
    // },
}));

export type FormLayoutMode = 'page' | 'guide' | 'dialog' | 'inline';

export type FormLayoutProps = FormButtonsProps &
    Omit<TitleAndSubTitleProps, 'sx'> & {
        mode?: FormLayoutMode;

        /**
         * Indicates that the form automatically saves all changes and there is no need for a Save button. Instead, the
         * button will use a "Done" button instead if none is provided
         */
        usesAutoSave?: boolean;

        // These are new, and I think they will cover our use-cases pretty well. Some forms uses auto-saving, and they don't
        // need a submit button. Most forms does require a save function
        /**
         * The user clicks on save, continue or done
         */
        onSubmitSuccess?(): void;

        onContinue?(): Promise<void>;

        /**
         * The user clicks cancel, back or closes the dialog
         */
        onLeaveForm?(reason: 'clickBack' | 'clickNext' | 'closeDialog'): void;

        // submitButtonLabel

        dialogProps?: Partial<DialogProps>;
        children: ReactNode;
        fullHeight?: boolean;
    };
//
// export type FormLayoutPropsForGuideMode = FormLayoutBaseProps & {
//     mode: 'guide';
//     onNext(): void;
//     onPrev(): void;
// };
// export type FormLayoutPropsForPageMode = FormLayoutBaseProps & {
//     mode?: 'page'; // default
//     onSubmitSuccess?(): void;
//     onLeavePage(): void;
// };
// export type FormLayoutPropsForDialogMode = FormLayoutBaseProps & {
//     mode: 'dialog';
//     onSubmitSuccess?(): void;
//     onCloseDialog(): void;
// };

// export type FormLayoutProps =
//     | FormLayoutPropsForGuideMode
//     | FormLayoutPropsForPageMode
//     | FormLayoutPropsForDialogMode;

export function FormLayout(props: FormLayoutProps) {
    useScrollToTop();
    const isSmallScreen = useIsScreenSmallerThanBreakpoint('sm');
    const { formatMessage } = useIntl();
    const {
        mode = 'page',
        title,
        subTitle,
        preTitle,
        children,
        fullHeight = false,
        usesAutoSave,
        ...formButtonsProps
    } = props;

    const { continueButtonProps, goBackButtonProps, ...otherFormButtonProps } =
        formButtonsProps;

    switch (mode) {
        case 'guide':
            continueButtonProps.endIcon ??= <ArrowRightIcon size={12} />;
            return (
                <Root>
                    {(title || subTitle) && (
                        <TitleAndSubTitle
                            title={title}
                            subTitle={subTitle}
                            preTitle={preTitle}
                        />
                    )}
                    <Content
                        fullHeight={fullHeight}
                        sx={title || subTitle ? undefined : { pt: 0 }}
                    >
                        {children}
                    </Content>
                    <FormPageFooter>
                        <FormPageFooterContent
                            sx={(theme) => ({
                                [`& .${formButtonClasses.saveButton}`]: {
                                    [theme.breakpoints.up('sm')]: {
                                        minWidth: 220,
                                    },
                                },
                            })}
                        >
                            <FormButtons
                                continueButtonProps={continueButtonProps}
                                goBackButtonProps={goBackButtonProps}
                                {...otherFormButtonProps}
                                /* In guide-mode, we should always be able to continue */
                                allowContinueIfPristine
                            />
                        </FormPageFooterContent>
                    </FormPageFooter>
                </Root>
            );

        case 'page':
        case 'inline': {
            if (usesAutoSave) {
                continueButtonProps.children ??= formatMessage(doneMessage);
            }
            const RootComponent = mode === 'inline' ? 'div' : Root;
            return (
                <RootComponent>
                    {(title || subTitle) && (
                        <TitleAndSubTitle title={title} subTitle={subTitle} />
                    )}
                    <Content
                        fullHeight={fullHeight}
                        sx={title || subTitle ? undefined : { pt: 0 }}
                    >
                        {children}
                    </Content>
                    <FormPageFooter>
                        <FormPageFooterContent>
                            <FormButtons
                                continueButtonProps={continueButtonProps}
                                goBackButtonProps={goBackButtonProps}
                                {...otherFormButtonProps}
                            />
                        </FormPageFooterContent>
                    </FormPageFooter>
                </RootComponent>
            );
        }
        case 'dialog':
            if (usesAutoSave) {
                continueButtonProps.children ??= formatMessage(doneMessage);
            }
            if (goBackButtonProps) {
                goBackButtonProps.children ??=
                    formatMessage(closeDialogMessage);
            }

            return (
                <Dialog
                    open
                    maxWidth={'md'}
                    fullWidth
                    fullScreen={isSmallScreen}
                    {...props.dialogProps}
                    //onClose={formButtonsProps.goBackButtonProps?.onClick}
                >
                    <DialogTitle
                        display="flex"
                        justifyContent="space-between"
                        alignContent="flex-start"
                        // component={Title}
                    >
                        <span>{title}</span>
                        {(formButtonsProps.goBackButtonProps?.onClick ||
                            usesAutoSave) && (
                            <IconButton
                                aria-label="close"
                                onClick={
                                    (usesAutoSave
                                        ? (
                                              formButtonsProps.continueButtonProps as any
                                          ).onClick
                                        : null) ??
                                    formButtonsProps.goBackButtonProps.onClick
                                }
                                sx={(theme) => ({
                                    position: 'absolute',
                                    right: theme.spacing(1),
                                    top: theme.spacing(1),
                                    color: (theme) => theme.palette.grey[500],
                                })}
                            >
                                <CloseIcon />
                            </IconButton>
                        )}
                    </DialogTitle>
                    <DialogContent dividers={true}>
                        {subTitle && (
                            <SubTitle sx={{ pb: 3, pt: 0 }}>
                                {subTitle}
                            </SubTitle>
                        )}
                        {children}
                    </DialogContent>
                    <DialogActions
                        sx={{
                            [`& > .${formButtonClasses.root}`]: {
                                justifyContent: 'flex-end',
                            },
                        }}
                    >
                        <FormButtons
                            goBackButtonProps={
                                goBackButtonProps?.onClick
                                    ? {
                                          children:
                                              formatMessage(closeDialogMessage),
                                          ...goBackButtonProps,
                                      }
                                    : undefined
                            }
                            continueButtonProps={{
                                children: formatMessage(saveButtonMessage),
                                ...continueButtonProps,
                            }}
                            {...otherFormButtonProps}
                        />
                    </DialogActions>
                </Dialog>
            );
    }
}
